import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import i18n from '@batical/i18n'
import { selectCalculCode, useContextValue } from '@batical/reducer/src'
import { CalculCodeStateKeys } from '@batical/types/src'
import { FaChevronRight } from 'react-icons/fa'
import { useSelector } from 'react-redux'

import { ViewLine, CalculText, CalculTextInput, ViewDisclosure } from './LineStyleComponent'
import TextInputAdornment from './TextInputAdornment'

const isDouble = (value: unknown) => {
  return typeof value === 'number' || value instanceof Number
}
export type TextInputLineProps = {
  title?: string
  calculCode: CalculCodeStateKeys
  /**
   * Unit value to display
   *
   * @type {string}
   */
  unit?: string
  /**
   * Should show a disclosure button on right side
   *
   * @type {boolean}
   */
  disclosure?: boolean

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDisclosurePress?: (value: number, meta: any) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  image?: any
  /**
   * Input Value can be negative
   *
   * @type {boolean}
   */
  negativeValue?: boolean
  /**
   *
   *
   * @type {number}
   */
  factor?: number
  /**
   * name of the variable in redux store context to save
   *
   * @type {string}
   */
  variableName: string

  /**
   * action to do on change value
   *
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (number: number, value: string, extra?: any) => void
  extraParam?: Object //category can be used for disclosure, additional parameter for on change on disclosure
  /**
   * launch calcul
   *
   * @type {boolean}
   */
  doCalcul?: boolean
  /**
   *
   * if true element is the first
   * @type {boolean}
   */
  first?: boolean
  /**
   *
   *if true element is the last
   * @type {boolean}
   */
  last?: boolean
}

const TextInputLine = ({
  calculCode,
  variableName,
  unit,
  disclosure = false,
  onChange,
  extraParam = undefined,
  title,
  factor = 1,
  doCalcul = false,
  first = false,
  last = false
}: TextInputLineProps) => {
  const code = useSelector(selectCalculCode)

  const [value, update] = useContextValue(variableName, calculCode, doCalcul)

  const textInputRef = useRef(null)
  const [inputVal, setInputVal] = useState('0')

  const text = useMemo(() => {
    let tex = ''
    if (title) {
      tex = i18n.t(title)
    } else {
      tex = i18n.t(`${calculCode ?? code}_${variableName}`)
    }

    return tex
  }, [calculCode, code, title, variableName])

  useEffect(() => {
    if (isDouble(value)) {
      setInputVal(String(+value * factor))
    }
  }, [value, factor])

  const _onEndEditing = useCallback(() => {
    const result = parseFloat(inputVal.replace(',', '.'))
    if (isNaN(result)) {
      setInputVal(String(+value))

      if (onChange) {
        onChange(0, variableName, extraParam)
      } else {
        update(0)
      }
    } else if (String(value) === inputVal) {
      // console.error('textinputline, I donw now why this condition but should not happen')
      // i saw it happen so it is required but did not analyse why
      if (onChange) {
        onChange(value / factor, variableName, extraParam)
      } else {
        update(value / factor)
      }

      setInputVal(inputVal)
    } else {
      if (onChange) {
        onChange(result / factor, variableName, extraParam)
      } else {
        update(result / factor)
      }
    }
  }, [extraParam, factor, inputVal, onChange, update, value, variableName])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const _onChangeText = useCallback((event: any) => {
    setInputVal(event.target.value)
  }, [])

  const onFocus = useCallback(() => {
    setInputVal('')
  }, [])

  // const _onTouchCell = useCallback(() => {
  //   if (onDisclosurePress != null) {
  //     let val = 0 || parseFloat(inputVal.replace(',', '.'))
  //     // if (value != null) {
  //     onDisclosurePress(val, extraParam)
  //     //  }
  //   } else {
  //     //textInputRef?.current?.focus()
  //   }
  // }, [extraParam, inputVal, onDisclosurePress])
  const onKeypress = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: any) => {
      if (e.key === 'Enter') {
        _onEndEditing()
      }
    },
    [_onEndEditing]
  )
  return (
    <ViewLine first={first} last={last}>
      <CalculText>{text}</CalculText>
      <ViewDisclosure>
        <CalculTextInput
          ref={textInputRef}
          value={inputVal}
          onFocus={onFocus}
          onChange={_onChangeText}
          onKeyPress={onKeypress}
          onBlur={_onEndEditing}
          variant="outlined"
          size="small"
          InputProps={{
            endAdornment: <TextInputAdornment text={unit} />
          }}
        />

        {disclosure && <FaChevronRight color="#94a3b8" size={16} />}
      </ViewDisclosure>
    </ViewLine>
  )
}

export default memo(TextInputLine)
